import {
  ERROR_AMOUNT_SOURCES,
  buildOtherDeductionOccurrence,
  checkOtherDeductionOccurrences,
  findOtherDeduction,
} from "score-common";

// #3153 - scoreadmin-fe's side of recording a per-occurrence catalog
// `otherDeductions` entry (engine: #1630, packages/score-common/src/utils/
// other-deductions.js). Same idea as scribe-fe's util/otherDeductions.js
// (#3152), kept as its own copy: an app does not import across apps.
//
// Entries are NEVER hand-built here: planOtherDeduction goes through
// buildOtherDeductionOccurrence, then re-judges the would-be errors[] with
// checkOtherDeductionOccurrences, the SAME check score-api runs on
// create/PUT/resolve. So the admin is told in words, before saving, what the
// server would otherwise answer with a bare 400.

/**
 * The catalog entries an admin may record on this test. A `cumulative: true`
 * entry is a ladder tier that belongs to errorRule (findOtherDeduction refuses
 * it), so it is not offered either.
 *
 * @param {object|null} test
 * @returns {Array<object>}
 */
export function recordableOtherDeductions(test) {
  const list = Array.isArray(test?.otherDeductions) ? test.otherDeductions : [];
  return list.filter(
    (d) => typeof d?.label === "string" && d.label !== "" && findOtherDeduction(test, d.label),
  );
}

/** How many occurrences of `label` the sheet already carries. */
export function recordedOtherDeductionCount(errors, label) {
  return (errors ?? []).filter(
    (e) => e?.amountSource === ERROR_AMOUNT_SOURCES.OTHER_DEDUCTION && e.label === label,
  ).length;
}

/**
 * Decide what recording one occurrence of `label` would do.
 *
 * @param {object} args
 * @param {object|null} args.test - catalog Test row (carries otherDeductions once enriched)
 * @param {string} args.label
 * @param {Array<object>} args.errors - the sheet's current errors[]
 * @param {{ id: string, movementNumber?: number|null }} args.occurrence
 * @returns {{ ok: true, entry: object }
 *   | { ok: false, reason: "notEnriched" | "unknown" | "mismatch" | "cap", label: string, max: number|null }}
 */
export function planOtherDeduction({ test, label, errors, occurrence }) {
  const fail = (reason) => ({
    ok: false,
    reason,
    label,
    max: findOtherDeduction(test, label)?.maxOccurrences ?? null,
  });
  const built = buildOtherDeductionOccurrence(test, label, errors, occurrence);
  if (!built.ok) {
    const noTable = !Array.isArray(test?.otherDeductions) || test.otherDeductions.length === 0;
    return fail(noTable ? "notEnriched" : built.reason);
  }
  const problems = checkOtherDeductionOccurrences(test, [...(errors ?? []), built.entry], errors ?? []);
  if (problems.length > 0) return fail(problems[0].reason);
  return { ok: true, entry: built.entry };
}
